import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

type SortDirection = 'asc' | 'desc';

interface SortConfig<K extends string> {
  key: K | null;
  direction: SortDirection;
}

interface UseSortableResultsOptions {
  numericKeys?: readonly string[];
}

export function useSortableResults<T, K extends string = string>(
  results: T[],
  options: UseSortableResultsOptions = {},
) {
  const { numericKeys = ['similarity'] } = options;
  const [sortConfig, setSortConfig] = useState<SortConfig<K>>({ key: null, direction: 'asc' });

  const sortedResults = useMemo(() => {
    if (!sortConfig.key) return results;
    const key = sortConfig.key as string;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const sorted = [...results];
    sorted.sort((a, b) => {
      if (key === 'index') return 0;
      let av: number | string = (a as any)[key];
      let bv: number | string = (b as any)[key];
      if (numericKeys.includes(key)) {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
      } else if (typeof av === 'string' && typeof bv === 'string') {
        av = av.toLowerCase();
        bv = bv.toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [results, sortConfig, numericKeys]);

  const toggleSort = (key: K) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  };

  const SortIcon = ({ column }: { column: K }) => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown className="ml-1 h-3.5 w-3.5 opacity-50" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="ml-1 h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="ml-1 h-3.5 w-3.5" />
    );
  };

  const HeaderButton = ({
    column,
    label,
    align = 'left',
  }: {
    column: K;
    label: string;
    align?: 'left' | 'center';
  }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => toggleSort(column)}
      className={`-ml-2 h-8 px-2 text-xs font-medium uppercase tracking-wide ${
        align === 'center' ? 'mx-auto flex' : ''
      }`}
    >
      {label}
      <SortIcon column={column} />
    </Button>
  );

  return { sortedResults, sortConfig, toggleSort, SortIcon, HeaderButton };
}

export function ScoreBar({ value }: { value: number | null }) {
  if (value == null) return null;
  const pct = Math.max(0, Math.min(1, value)) * 100;
  const fillColor =
    value >= 0.7 ? 'bg-emerald-500' : value >= 0.5 ? 'bg-amber-500' : 'bg-slate-400';
  return (
    <div className="h-1.5 w-10 overflow-hidden rounded-full bg-muted">
      <div className={`h-full rounded-full ${fillColor}`} style={{ width: `${pct}%` }} />
    </div>
  );
}
